import { useState, useEffect, useMemo } from "react";
import { useQuery, gql } from "@apollo/client";
import * as MENUS from "constants/menus";
import { Layout } from "features";
import { NavigationMenu } from "components";
import {
    SITE_SETTINGS_FRAGMENT,
    SEO_FRAGMENT,
} from "fragments";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Image from "next/image";
/* -----------------------------
   QUERY
------------------------------ */


const GET_LAYOUT = gql`
  query GalleryLayout(
    $headerLocation: MenuLocationEnum!
    $footerLocation: MenuLocationEnum!
  ) {
    siteSettings {
      ...SiteSettingsFragment
    }

    headerMenuItems: menuItems(
      where: { location: $headerLocation }
      first: 50
    ) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }

    footerMenuItems: menuItems(
      where: { location: $footerLocation }
      first: 50
    ) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }

  ${SITE_SETTINGS_FRAGMENT}
  ${NavigationMenu.fragments.entry}
`;

const GET_GALLERY = gql`
  query GalleryPage {
    page(id: "720", idType: DATABASE_ID) {
      seo {
        ...SEOFragment
      }

      gallery {
        galleries {
          galleryTitle
          location
          years
          images {
            image {
              node {
                altText
                sourceUrl(size: LARGE)
              }
            }
          }
        }
      }
    }
  }

  ${SEO_FRAGMENT}
`;


/* -----------------------------
   TYPES
------------------------------ */

type GalleryImage = {
    src: string;
    alt: string;
};


/* -----------------------------
   COMPONENT
------------------------------ */

const Component = () => {

    const [selectedGallery, setSelectedGallery] = useState<any>(null);
    const [lightboxIndex, setLightboxIndex] = useState(-1);

    const { data: layoutData } = useQuery(GET_LAYOUT, {
        variables: Component.variables(),
    });

    const {
        data: galleryData,
        loading,
        error,
    } = useQuery(GET_GALLERY);


    const galleries = useMemo(
        () => galleryData?.page?.gallery?.galleries || [],
        [galleryData]
    );


    useEffect(() => {
        if (!selectedGallery && galleries.length) {
            setSelectedGallery(galleries[0]);
        }
    }, [galleries, selectedGallery]);


    const galleryImages: GalleryImage[] =
        selectedGallery?.images
            ?.map((img: any) => {

                const node = img?.image?.node;

                if (!node?.sourceUrl) {
                    return null;
                }

                return {
                    src: node.sourceUrl,
                    alt: node.altText || "Gallery image",
                };

            })
            .filter(Boolean) || [];


    const {
        siteSettings,
        headerMenuItems,
        footerMenuItems,
    } = layoutData || {};


    const seo = {
        ...(galleryData?.page?.seo || {}),
        title: "Gallery - Beyer Cares Foundation",
    };


    const {
        address,
        logo,
        logoWhite,
        logoAlt,
        cta,
        turnOnAnnouncements,
        announcements,
    } = siteSettings?.general || {};


    return (
        <Layout
            headerMenuItems={headerMenuItems || { nodes: [] }}
            footerMenuItems={footerMenuItems || { nodes: [] }}
            seo={seo}
            logo={logo}
            logoWhite={logoWhite}
            logoAlt={logoAlt}
            cta={cta}
            address={address}
            turnOnAnnouncements={turnOnAnnouncements || false}
            announcements={announcements || []}
            title={siteSettings?.general?.title || ""}
            description={seo?.metaDesc || ""}
            phoneNumber={siteSettings?.general?.phone || ""}
            email={siteSettings?.general?.email || ""}
        >

            {error ? (

                /* ERROR */
                <div className="container mx-auto max-w-5xl px-4 py-20 text-center">
                    <h1 className="font-serif text-2xl md:text-4xl uppercase text-secondary">
                        Something went wrong
                    </h1>
                    <p className="mt-4 max-w-xl mx-auto text-gray-600 font-sans">
                        {error.message}
                    </p>
                </div>

            ) : loading ? (

                /* LOADING SKELETON */
                <div className="animate-pulse">

                    <div className="container mx-auto max-w-5xl px-4 py-10 text-center">
                        <div className="mx-auto h-8 w-32 rounded bg-gray-200" />
                        <div className="mx-auto mt-4 h-10 w-2/3 rounded bg-gray-200" />
                        <div className="mx-auto mt-4 h-5 w-1/2 rounded bg-gray-200" />
                    </div>

                    <div className="hidden md:flex justify-center flex-wrap gap-3 mb-10 px-4">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div
                                key={index}
                                className="h-10 min-w-[150px] rounded-lg bg-gray-200"
                            />
                        ))}
                    </div>

                    <div className="container mx-auto max-w-7xl px-4 pb-20">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {Array.from({ length: 8 }).map((_, index) => (
                                <div
                                    key={index}
                                    className="aspect-square rounded-xl bg-gray-200"
                                />
                            ))}
                        </div>
                    </div>

                </div>

            ) : (

                <>

            {/* HEADER */}
            <div className="container mx-auto max-w-5xl px-4 py-10 text-center">

                <h2 className="font-accent text-3xl font-bold text-secondary">
                    Gallery
                </h2>

                <h1 className="mt-3 font-serif text-xl md:text-5xl uppercase">
                    {selectedGallery?.galleryTitle}
                </h1>

                <p className="mt-4 max-w-xl mx-auto text-gray-600 font-sans text-lg">
                    {selectedGallery?.location}
                    <br></br>
                    {galleryImages.length} photos
                    {" · "}
                    {selectedGallery?.years}
                </p>

            </div>


            {/* DESKTOP NAV */}

            <div className="hidden md:flex justify-center flex-wrap gap-3 mb-10 px-4">

                {galleries.map((gallery: any, index: number) => (

                    <button
                        key={index}
                        onClick={() => setSelectedGallery(gallery)}
                        className={`px-4 py-2 rounded-lg font-bold border-2 transition-all duration-300 min-w-[150px] font-sans ${selectedGallery?.galleryTitle === gallery.galleryTitle
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-primary border-primary hover:bg-primary hover:text-white"
                            }`}
                    >
                        {gallery.galleryTitle}
                    </button>

                ))}

            </div>


            {/* MOBILE NAV */}

            <div className="md:hidden mb-8 border-t border-b">

                <select
                    value={selectedGallery?.galleryTitle || ""}
                    onChange={(e) => {

                        const gallery = galleries.find(
                            (item: any) =>
                                item.galleryTitle === e.target.value
                        );

                        if (gallery) {
                            setSelectedGallery(gallery);
                        }

                    }}
                    className="p-4 w-full font-sans text-lg text-secondary"
                >

                    {galleries.map((gallery: any, index: number) => (

                        <option
                            key={index}
                            value={gallery.galleryTitle}
                        >
                            {gallery.galleryTitle}
                        </option>

                    ))}

                </select>

            </div>


            {/* GRID */}

            <div className="container mx-auto max-w-7xl px-4 pb-20">

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">

                    {galleryImages.map((img, index) => (

                        <div
                            key={img.src}
                            onClick={() => setLightboxIndex(index)}
                            className="relative aspect-square overflow-hidden rounded-xl cursor-pointer bg-white shadow-md hover:shadow-xl transition-all"
                        >

                            <Image
                                src={img.src}
                                alt={img.alt}
                                fill
                                className="object-cover object-top hover:scale-105 transition-transform duration-300"
                                sizes="(max-width:1024px) 50vw, 25vw"
                            />

                        </div>

                    ))}

                </div>

            </div>
                </>
            )}

            {/* LIGHTBOX */}

            <Lightbox
                open={lightboxIndex >= 0}
                index={lightboxIndex}
                close={() => setLightboxIndex(-1)}
                slides={galleryImages.map((img) => ({
                    src: img.src,
                }))}
            />


        </Layout>
    );
};


export default Component;


/* -----------------------------
   VARIABLES
------------------------------ */

Component.variables = () => ({
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
});
